import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ShoppingBag,
  Store,
  Megaphone,
  ChevronDown,
  ChevronRight,
  Settings,
  MapPin,
  Lock,
  UserCircle,
  Package,
  MessageCircle,
  Eye,
  DollarSign,
  Heart,
  User,
  Gauge,
  Wallet,
  CreditCard
} from 'lucide-react';
import { Card } from './ui/card';

const AccountSidebar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    account: true,
    wallet: true,
    buyer: true,
    seller: false,
    advertiser: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const isActive = (path) => location.pathname === path;

  const MenuItem = ({ icon: Icon, label, path }) => (
    <button
      data-testid={`sidebar-link-${path.replace(/\//g, '-')}`}
      onClick={() => navigate(path)}
      className={`w-full flex items-center space-x-3 pl-12 pr-4 py-2.5 text-left transition-colors ${
        isActive(path)
          ? 'bg-zl-orange-50 text-zl-orange border-r-4 border-zl-orange'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );

  const SectionHeader = ({ icon: Icon, label, section }) => (
    <button
      data-testid={`sidebar-section-${section}`}
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between px-4 py-3 text-gray-900 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center space-x-3">
        <Icon className="w-5 h-5" />
        <span className="font-semibold text-sm">{label}</span>
      </div>
      {expandedSections[section] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
    </button>
  );

  return (
    <Card className="h-full sticky top-24 overflow-y-auto" data-testid="account-sidebar">
      {/* User Info */}
      <div className="p-4 border-b bg-gradient-to-r from-zl-orange-50 to-orange-50/30">
        <div className="flex items-center space-x-3">
          <div className="w-11 h-11 bg-zl-orange rounded-full flex items-center justify-center text-white font-bold text-lg">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
        </div>
      </div>

      <div className="py-1">
        {/* Mi Billetera — CATEGORIA PRINCIPAL */}
        <div className="border-b">
          <button
            data-testid="sidebar-section-wallet"
            onClick={() => navigate('/account/wallet')}
            className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
              isActive('/account/wallet')
                ? 'bg-zl-orange-50 text-zl-orange border-r-4 border-zl-orange'
                : 'text-gray-900 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <Wallet className="w-5 h-5" />
              <span className="font-semibold text-sm">Mi Billetera</span>
            </div>
          </button>
        </div>

        {/* Mi Cuenta */}
        <div className="border-b">
          <SectionHeader icon={UserCircle} label="Mi Cuenta" section="account" />
          {expandedSections.account && (
            <div className="bg-gray-50/60">
              <MenuItem icon={User} label="Información Personal" path="/account/personal" />
              <MenuItem icon={Settings} label="Datos de la Cuenta" path="/account/details" />
              <MenuItem icon={Lock} label="Seguridad" path="/account/security" />
              <MenuItem icon={MapPin} label="Direcciones" path="/account/addresses" />
              <MenuItem icon={Eye} label="Privacidad" path="/account/privacy" />
              <MenuItem icon={CreditCard} label="Mi Suscripción" path="/account/subscription" />
            </div>
          )}
        </div>

        {/* Comprador */}
        <div className="border-b">
          <SectionHeader icon={ShoppingBag} label="Comprador" section="buyer" />
          {expandedSections.buyer && (
            <div className="bg-gray-50/60">
              <MenuItem icon={ShoppingBag} label="Mis Compras" path="/my-purchases" />
              <MenuItem icon={Heart} label="Favoritos" path="/buyer/favorites" />
            </div>
          )}
        </div>

        {/* Vendedor */}
        <div className="border-b">
          <SectionHeader icon={Store} label="Vendedor" section="seller" />
          {expandedSections.seller && (
            <div className="bg-gray-50/60">
              <MenuItem icon={Gauge} label="Centro de Control" path="/seller" />
              <MenuItem icon={Package} label="Mis Publicaciones" path="/seller/products" />
              <MenuItem icon={DollarSign} label="Mis Ventas" path="/seller/orders" />
              <MenuItem icon={MessageCircle} label="Preguntas" path="/seller/questions" />
              <MenuItem icon={Store} label="Configurar Tienda" path="/store-config" />
            </div>
          )}
        </div>

        {/* Anunciante */}
        <div className="border-b">
          <SectionHeader icon={Megaphone} label="Anunciante" section="advertiser" />
          {expandedSections.advertiser && (
            <div className="bg-gray-50/60">
              <MenuItem icon={Megaphone} label="Mis Campañas" path="/advertiser/campaigns" />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AccountSidebar;
